<template>
  <v-container>
    <!-- Header Section -->
    <v-row class="mb-6 align-center">
      <v-col cols="12" md="7">
        <h1 class="text-h4 font-weight-bold d-flex align-center">
          <v-icon large color="primary" class="mr-3">fas fa-city</v-icon>
          Clientes
        </h1>
        <div class="text-caption grey--text text--darken-1 mt-1">
          Gestión integral de la base de datos de clientes y sucursales
        </div>
      </v-col>
      <v-col cols="12" md="5" class="text-right d-flex justify-end align-center">
        <import-customer-button v-if="factronicaEnabled" class="mr-2" />
        
        <v-btn to="customers/configuration" class="mr-2" color="primary" outlined depressed v-if="$can('create', 'Customer')">
          <v-icon left small>fas fa-cog</v-icon>
          Mantenedores
        </v-btn>

        <v-btn to="customers/import" class="mr-2" color="primary" outlined depressed v-if="$can('create', 'Customer')">
          <v-icon left small>fas fa-file-import</v-icon>
          Importar
        </v-btn>

        <export-customers :filters="filters" :options="options" :disabled="loading" :search="search" v-if="$can('export', 'Customer')" class="mr-2" />

        <v-btn v-if="$can('create', 'Customer')" color="primary" depressed @click.stop="CreateUpdate" class="px-6">
          <v-icon left small>fas fa-plus</v-icon>
          Nuevo Cliente
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card outlined class="bg-modern-filters pa-6 mb-6 border-dashed">
      <v-row dense>
        <v-col cols="12" md="4">
          <tipo-selection 
            v-model="filters.tipo"
            @change="fetch()"
            :filtro="true"
          />
        </v-col>
        <v-col cols="12" md="4">
          <industry-selection 
            v-model="filters.industry"
            @change="fetch()"
            :filtro="true"
          />
        </v-col>
        <v-col cols="12" md="4">
          <rubro-selection 
            v-model="filters.rubro"
            @change="fetch()"
            :filtro="true"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Table Section -->
    <v-card outlined class="modern-card elevation-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalCustomers"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <template v-slot:top>
          <v-divider></v-divider>
          <div class="px-6 py-4 grey lighten-5">
            <v-row dense justify="end">
              <v-col cols="12" md="6" lg="4">
                <v-text-field
                  v-model="search"
                  @keyup="searchTimeOut"
                  label="Buscar por RUT, Nombre o Vendedor"
                  prepend-inner-icon="fas fa-search"
                  outlined
                  dense
                  hide-details
                  :disabled="loading"
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
        </template>

        <template v-slot:item.rut="{ item }">
          <span class="font-weight-bold grey--text text--darken-3">{{ item.rut }}</span>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold primary--text">{{ item.name }}</span>
            <span v-if="item.branch_office > 0" class="caption grey--text">
              <v-icon x-small class="mr-1">fas fa-code-branch</v-icon>Sucursal #{{ item.branch_office }}
            </span>
          </div>
        </template>

        <template v-slot:item.fantasy="{ item }">
          <span class="body-2">{{ item.fantasy }}</span>
        </template>

        <template v-slot:item.tipo="{ item }">
          <v-chip x-small outlined label color="grey darken-1">{{ item.tipo }}</v-chip>
        </template>

        <template v-slot:item.seller="{ item }">
          <div v-if="item.seller" class="d-flex align-center">
            <v-icon x-small class="mr-1 grey--text">fas fa-user-tie</v-icon>
            <span class="caption">{{ item.seller.first_name }} {{ item.seller.last_name }}</span>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :color="item.status ? 'success' : 'error'"
            x-small
            label
            outlined
            class="font-weight-bold"
          >
            {{ item.status ? 'ACTIVO' : 'INACTIVO' }}
          </v-chip>
        </template>

        <template v-slot:item.relbase_customer_id="{ item }">
          <v-chip
            v-if="relbaseEnabled && item.relbase_customer_id"
            x-small
            label
            color="#35BDC8"
            dark
            class="font-weight-bold"
          >
            <v-icon x-small left>mdi-check-circle</v-icon>
            Sincronizado
          </v-chip>
          <v-chip
            v-else-if="relbaseEnabled"
            x-small
            label
            outlined
            color="grey"
          >
            Sin sincronizar
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                small 
                icon 
                color="teal" 
                class="mr-1"
                @click.stop="openHistory(item.id)"
              >
                <v-icon small>fas fa-chart-line</v-icon>
              </v-btn>
            </template>
            <span>Ficha 360° e Histórico</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                v-bind="attrs"
                v-on="on"
                v-if="$can('read', subject('Customer', item))" 
                small 
                icon 
                color="primary" 
                @click.stop="CreateUpdate(item)"
              >
                <v-icon small>fas fa-edit</v-icon>
              </v-btn>
            </template>
            <span>Editar Cliente</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="showDetail" max-width="500px">
      <v-card v-if="selected" outlined class="modern-card overflow-hidden">
        <customer-card :selected="selected" @close="showDetail = false"/>
      </v-card>
    </v-dialog>

    <!-- 360° History Modal -->
    <customer-history-modal
      v-model="showHistory"
      :customer-id="selectedHistoryCustomerId"
    />
  </v-container>
</template>

<script>
import { fetchCustomersQuery } from '@/api/customers'
import CustomerCard from '@/components/customers/CustomerCard'
import ImportCustomerButton from '@/components/factronica/ImportCustomerButton'
import notifications from '@/utils/notifications'
import ExportCustomers from '@/components/customers/ExportCustomers'
import TipoSelection from '@/components/customers/TipoSelection.vue'
import IndustrySelection from '@/components/customers/IndustrySelection.vue'
import RubroSelection from '@/components/customers/RubroSelection.vue'
import CustomerHistoryModal from '@/components/customers/CustomerHistoryModal.vue'
import { subject } from '@casl/ability';
import tipoCustomer from '@/utils/tipoCustomer'

export default {
  name: 'Customers',
  components: {
    CustomerCard,
    CustomerHistoryModal,
    ImportCustomerButton,
    TipoSelection,
    IndustrySelection,
    RubroSelection,
    ExportCustomers
  },
  data: () => ({
    items: [],
    headers: [
      { text: 'RUT', value: 'rut', align: 'start', width: '120' },
      { text: 'Razón Social', value: 'name', align: 'start' },
      { text: 'Nombre Fantasía', value: 'fantasy', align: 'start' },
      { text: 'Tipo', value: 'tipo', align: 'start', width: '120' },
      { text: 'Vendedor', value: 'seller', align: 'start', width: '180' },
      { text: 'Estado', value: 'status', align: 'center', width: '100', sortable: false },
      { text: 'Relbase', value: 'relbase_customer_id', align: 'center', width: '130', sortable: false },
      { text: 'Acciones', value: 'actions', align: 'center', width: '100', sortable: false }
    ],
    search: '',
    filters: {
      tipo: '',
      industry: '',
      rubro: ''
    },
    timer: null,
    options: {},
    totalCustomers: 0,
    showDetail: false,
    showHistory: false,
    selectedHistoryCustomerId: null,
    loading: false,
    selected: null,
  }),
  methods: {
    subject: subject,
    openHistory(id) {
      this.selectedHistoryCustomerId = id
      this.showHistory = true
    },
    showCustomer(item) {
      this.selected = item
      this.showDetail = true
    },
    searchTimeOut() {  
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
    CreateUpdate(item) {
      this.$router.push({ name: 'CustomersCreateUpdate', params: { customerId: item.id } })
    },
    async fetch() {
      if (this.loading) return
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      
      let sort = ''
      if (sortBy && sortBy.length > 0) {
        if (sortBy[0] == 'seller') sort = 'seller__first_name'
        else sort = sortBy[0]
        if (sortDesc[0]) sort = '-' + sort
      }

      let query = `ordering=${sort}&page=${page || 1}&page_size=${itemsPerPage || 10}&search=${this.search || ''}&tipo=${this.filters.tipo || ''}`
      
      if (!this.$can('read_all', 'Customer')) {
        query += `&seller=${this.$store.getters.getUser.id}`
      }
      if (this.filters.industry) query += `&industry=${this.filters.industry}`
      if (this.filters.rubro) query += `&rubro=${this.filters.rubro}`
      
      await fetchCustomersQuery(query)
      .then((data) => {
        this.totalCustomers = data.count
        this.items = data.results
      })
      .catch((err) => {
        notifications.showError('Error cargando la lista de clientes: ' + err)
      })
      .finally(() => {
        this.loading = false
      })
    }
  },
  computed: {
    factronicaEnabled() {
      return this.$store.getters.getCompany.factronica_integration_enabled
    },
    relbaseEnabled() {
      return this.$store.getters.getCompany.relbase_integration_enabled
    },
    tipoCustomer() {
      return tipoCustomer
    }
  },
  watch: {
    options: {
      handler: 'fetch',
      deep: true
    }
  }
}
</script>

<style scoped>
.bg-modern-filters {
  background-color: #fcfcfc;
}
.border-dashed {
  border-style: dashed !important;
  border-width: 1px !important;
}
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
}
</style>
